/**
 * Admin (gear) navigation model — the single source of truth for
 * the split-pane admin: which groups exist, which existing pages
 * live under each one, and how a URL maps back to a group + tab.
 *
 * The admin pages themselves keep their original routes (/hr,
 * /time-attendance/policies, /hiring/templates, ...) so every
 * existing link + bookmark keeps working. This file just tells the
 * shell "this path belongs to Admin → Time & Attendance → Policies".
 */
import type { OrgModuleKey } from '@medlive-hris/shared-types'

export interface AdminTab {
  label: string
  route: string
  /** One-liner shown on the module overview page. */
  desc: string
  icon: string
  /** Extra path prefixes that should light this tab (detail pages). */
  prefixes?: string[]
}

export interface AdminGroup {
  key: 'org' | OrgModuleKey | 'roles' | 'compensation' | 'compliance'
  label: string
  icon: string
  accent: string
  desc: string
  /** Set for groups that map to a switchable module. */
  module?: OrgModuleKey
  /** Section header in the left list. */
  section: 'Organization' | 'Modules' | 'Company'
  /** Roles that may open this group. Default: HR only. */
  roles?: Array<'hr' | 'finance'>
  tabs: AdminTab[]
}

export const ADMIN_GROUPS: AdminGroup[] = [
  {
    key: 'org', label: 'Organization', icon: 'fas fa-building', accent: '#cb31ae', section: 'Organization',
    desc: 'HR overview and company-wide settings',
    tabs: [
      { label: 'HR overview', route: '/admin/overview', icon: 'fas fa-gauge', desc: 'Cycle progress and HR entry points.' },
      { label: 'Modules', route: '/admin/modules', icon: 'fas fa-toggle-on', desc: 'Choose which modules the rest of the organization can see.' },
    ],
  },
  {
    key: 'reviews', module: 'reviews', label: 'Reviews', icon: 'fas fa-clipboard-check', accent: '#0891b2', section: 'Modules',
    desc: 'Self-reviews, manager reviews, calibration',
    // Same three slots as Roles: by employee · cycles · incomplete.
    tabs: [
      { label: 'Overview', route: '/admin/reviews', icon: 'fas fa-gauge', desc: '' },
      { label: 'Employee Reviews', route: '/admin/reviews/employees', icon: 'fas fa-user-check', desc: 'Every employee\'s review history across all cycles.' },
      { label: 'Review cycles', route: '/hr', icon: 'fas fa-clock', desc: 'Create, launch, calibrate, and close review cycles.',
        prefixes: ['/hr/cycle', '/hr/calibrat', '/hr/edit-review', '/hr/reviews/'] },
      { label: 'Incomplete reviews', route: '/hr/incomplete', icon: 'fas fa-triangle-exclamation', desc: 'Reviews still outstanding in the active cycle, by stage.' },
    ],
  },
  {
    // Job descriptions live on their own: reviews read the JD of record,
    // but JDs are maintained (and approved) year-round, cycle or not.
    // Not a switchable module — every employee always has a role.
    // Same three slots as Reviews: by employee · cycles · incomplete.
    key: 'roles', label: 'Roles', icon: 'fas fa-file-lines', accent: '#cb31ae', section: 'Modules',
    desc: 'Job descriptions and JD updates',
    tabs: [
      { label: 'Overview', route: '/admin/roles', icon: 'fas fa-gauge', desc: '' },
      { label: 'Job descriptions', route: '/hr/jds', icon: 'fas fa-file-lines', desc: 'Every employee\'s current JD and status (pending, sent back, needs JD, stale), open changes, and JD update history.', prefixes: ['/hr/jds'] },
      { label: 'JD update cycles', route: '/admin/roles/jd-update-cycles', icon: 'fas fa-clipboard-check', desc: 'Required, dated JD updates: managers confirm or update every JD by a due date.', prefixes: ['/admin/roles/jd-update-cycles/'] },
      { label: 'Incomplete JD updates', route: '/admin/roles/incomplete', prefixes: ['/admin/roles/approvals'], icon: 'fas fa-triangle-exclamation', desc: 'What\'s still open in a JD update cycle, by stage.' },
    ],
  },
  {
    // Compensation: HRIS is the comp system of record. Finance sees this
    // group (and only this group); HR sees it alongside everything else.
    key: 'compensation', module: 'comp', label: 'Compensation', icon: 'fas fa-sack-dollar', accent: '#16a34a', section: 'Modules',
    desc: 'Comp data, annual increase cycles, Paylocity export',
    roles: ['hr', 'finance'],
    tabs: [
      { label: 'Overview', route: '/admin/compensation', icon: 'fas fa-gauge', desc: '' },
      { label: 'Comp cycles', route: '/admin/compensation/cycles', icon: 'fas fa-clock', desc: 'Annual increase cycles: executives allocate, the CEO approves, finance exports.', prefixes: ['/admin/compensation/cycles/'] },
      { label: 'Compensation data', route: '/admin/compensation/data', icon: 'fas fa-table', desc: 'Base, variable and commission per person, Paylocity IDs, bulk import.', prefixes: ['/admin/compensation/data/'] },
      { label: 'Paylocity export', route: '/admin/compensation/export', icon: 'fas fa-file-export', desc: 'Web Link Pay Rates import file and the finance summary for a closed cycle.' },
      { label: 'Settings', route: '/admin/compensation/settings', icon: 'fas fa-gear', desc: 'Paylocity company id, pay frequency, change reason, access.' },
    ],
  },
  {
    key: 'ta', module: 'ta', label: 'Time & Attendance', icon: 'fas fa-calendar-days', accent: '#0d9488', section: 'Modules',
    desc: 'Time off requests, balances, team calendar',
    tabs: [
      { label: 'Overview', route: '/admin/ta', icon: 'fas fa-gauge', desc: '' },
      { label: 'Policies', route: '/time-attendance/policies', icon: 'fas fa-scale-balanced', desc: 'Accrual policies for PTO, sick, and personal time.' },
      { label: 'Assignments', route: '/time-attendance/assignments', icon: 'fas fa-user-tag', desc: 'Which policy each employee is on, plus bulk apply.' },
    ],
  },
  {
    key: 'hiring', module: 'hiring', label: 'Hiring', icon: 'fas fa-briefcase', accent: '#7715c0', section: 'Modules',
    desc: 'Openings, applicant review, careers page',
    tabs: [
      { label: 'Overview', route: '/admin/hiring', icon: 'fas fa-gauge', desc: '' },
      { label: 'Email templates', route: '/hiring/templates', icon: 'fas fa-envelope', desc: 'Emails sent to candidates at each stage.' },
      { label: 'Question bank', route: '/hiring/questions', icon: 'fas fa-list-check', desc: 'Reusable application questions for openings.' },
      { label: 'Careers page', route: '/hiring/careers', icon: 'fas fa-globe', desc: 'The public careers page applicants see.' },
      { label: 'Team & access', route: '/hiring/settings', icon: 'fas fa-user-shield', desc: 'Who can create openings and review applicants.' },
    ],
  },
  {
    key: 'people', module: 'people', label: 'People', icon: 'fas fa-users', accent: '#16a34a', section: 'Modules',
    desc: 'Directory and org chart',
    tabs: [
      { label: 'Overview', route: '/admin/people', icon: 'fas fa-gauge', desc: '' },
      { label: 'Profile changes', route: '/admin/people/profile-changes', icon: 'fas fa-address-card', desc: 'Self-service profile edits to sync to payroll and benefits.' },
      { label: 'Data sync', route: '/admin/people/sync', icon: 'fas fa-arrows-rotate', desc: 'Sync from Microsoft 365 and import hire dates from BambooHR.' },
      { label: 'Hidden users', route: '/hr/hidden', icon: 'fas fa-eye-slash', desc: 'Accounts hidden from the directory and reviews.' },
    ],
  },
  {
    key: 'compliance', label: 'Compliance', icon: 'fas fa-shield-halved', accent: '#f59e0b', section: 'Company',
    desc: 'Personnel files and audit trail',
    tabs: [
      { label: 'Personnel files', route: '/hr/personnel-files', icon: 'fas fa-folder-open', desc: 'Signed and released documents per employee.' },
      { label: 'Audit log', route: '/hr/audit', icon: 'fas fa-shield-halved', desc: 'Every change, who made it, and when.' },
    ],
  },
]

export function adminGroupByKey(key: string): AdminGroup | undefined {
  return ADMIN_GROUPS.find(g => g.key === key)
}

/**
 * Groups a caller may open: HR → all; Finance-only → groups listing
 * 'finance' whose module is switched on (a switched-off module is HR-only).
 */
export function adminGroupsFor(opts: { hr: boolean; finance: boolean; isEnabled?: (m: OrgModuleKey) => boolean }): AdminGroup[] {
  if (opts.hr) return ADMIN_GROUPS
  if (opts.finance) return ADMIN_GROUPS.filter(g => g.roles?.includes('finance') && (!g.module || !opts.isEnabled || opts.isEnabled(g.module)))
  return []
}

/**
 * Which admin group + tab owns a path. Exact route matches win over
 * prefix matches so `/hr/hidden` lands on People, not Reviews.
 */
export function adminLocationForPath(path: string): { group: AdminGroup; tab: AdminTab | null } | null {
  if (path === '/admin' || path === '/admin/') return { group: ADMIN_GROUPS[0], tab: ADMIN_GROUPS[0].tabs[0] }
  for (const g of ADMIN_GROUPS) {
    const t = g.tabs.find(t => t.route === path)
    if (t) return { group: g, tab: t }
  }
  for (const g of ADMIN_GROUPS) {
    const t = g.tabs.find(t => t.prefixes?.some(p => path.startsWith(p)))
    if (t) return { group: g, tab: t }
  }
  // Anything else under /hr/ that isn't mapped yet still belongs in
  // Admin (it's all HR-only) — park it under Reviews.
  if (path.startsWith('/hr/')) return { group: adminGroupByKey('reviews')!, tab: null }
  return null
}

/**
 * Which switchable module a (non-admin) path belongs to. Used by the
 * route guard to keep non-HR users out of modules that are off.
 */
export function orgModuleForPath(path: string): OrgModuleKey | null {
  if (path.startsWith('/hiring')) return 'hiring'
  if (path.startsWith('/employee') || path.startsWith('/manager') || path.startsWith('/executive')) return 'reviews'
  if (path.startsWith('/people')) return 'people'
  if (path.startsWith('/time-attendance')) return 'ta'
  if (path.startsWith('/comp') || path.startsWith('/admin/compensation')) return 'comp'
  return null
}
